import React, { useMemo } from 'react';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import { GetSiblingProductsQuery, GetSiblingProductsQueryVariables } from 'src/generated/shopify-graphql';
import { prependLocaleToPath } from 'src/scripts/utils/localization';
import { notNil } from 'src/scripts/utils/typescriptUtils';
import { sortBy } from 'lodash-es';
import { IColorwayProductData } from '../../types/PDP';

const GetSiblingProductsQueryLoader = loader('./GetSiblingProducts.shopify.graphql');

const LengthToggleRefreshSolution = ({
  siblingsIdentifierTag,
  currentColor,
  currentHandle,
}: {
  siblingsIdentifierTag: string;
  currentColor: string;
  // for the refresh solution to find the "matching product" to get the length value
  currentHandle: string;
}) => {
  const { data, loading, error } = useQuery<GetSiblingProductsQuery, GetSiblingProductsQueryVariables>(
    GetSiblingProductsQueryLoader,
    { variables: { siblingIdQuery: `tag:'${siblingsIdentifierTag}'` } }
  );

  const lengthOptions = useMemo(() => {
    if (!data || loading || error || !currentColor) return [];
    return computeLengthOptions(data, currentColor);
  }, [data, loading, error, currentColor]);

  const currentInseamValue = lengthOptions.find((item) => item.handle === currentHandle)?.lengthLabel;

  if (!(lengthOptions.length > 1)) return null;
  return (
    <div className="LengthToggle d-flex flex-wrap" style={{ gap: 'var(--spacing-sm)' }}>
      <p className="body" style={{ flex: '100%' }}>
        <span className="body bold mr-2">Length:</span>
        {currentInseamValue ? `${currentInseamValue} Inseam` : null}
      </p>
      {lengthOptions.map((item) => {
        const active = item.handle === currentHandle;
        return (
          <a
            href={item.href}
            key={item.handle}
            className={`button-toggle link-styled-as-button ${active ? 'active' : ''}`}
            style={{
              textDecoration: 'none',
              minWidth: '116px',
              pointerEvents: active ? 'none' : 'auto',
            }}
            aria-disabled={active}
          >
            {item.buttonLabel}
          </a>
        );
      })}
    </div>
  );
};

export const LengthToggle = LengthToggleRefreshSolution;

// helper components
export const LengthTogglePdp = ({ colorwayProductData }: { colorwayProductData: IColorwayProductData }) => {
  const { tags, colorName, handle } = colorwayProductData;
  const siblingsIdentifierTag = tags?.find((tag) => tag.toLowerCase().startsWith(SiblingsTagPrefix));

  if (!siblingsIdentifierTag) return null;
  return (
    <LengthToggleRefreshSolution
      siblingsIdentifierTag={siblingsIdentifierTag}
      currentColor={colorName}
      currentHandle={handle}
    />
  );
};

// helpers
const computeLengthOptions = (data: GetSiblingProductsQuery, currentColor: string): LengthOption[] => {
  const products = data.products.edges.map((edge) => edge.node);

  const lengthOptions = products
    .filter((product) => {
      const isColorMatch = product.options
        .find((option) => option.name.toLowerCase() === 'color')
        ?.values.includes(currentColor);
      return isColorMatch && product.onlineStoreUrl;
    })
    .map((product) => {
      const lengthValue = product.options.find((option) => option.name.toLowerCase() === 'length')?.values?.[0];
      const { fit, inseam } = lengthValue ? destructureFitAndInseam(lengthValue) : { fit: null, inseam: null };
      return lengthValue && fit
        ? {
            lengthLabel: inseam,
            buttonLabel: fit,
            href: prependLocaleToPath(`/products/` + product.handle),
            handle: product.handle,
          }
        : null;
    })
    .filter(notNil);

  const combinedSortOrder = window?.server_pdp_data?.metaFields?.lengthToggleSortorder || [];
  const preferredSortOrder = combinedSortOrder.map((fit) => fit.toLowerCase());
  const sortedLengthOptions = sortBy(lengthOptions, (option) =>
    preferredSortOrder.indexOf(option.buttonLabel.toLowerCase())
  );

  return sortedLengthOptions;
};

type LengthOption = {
  lengthLabel: string | undefined;
  buttonLabel: string;
  href: string;
  handle: string;
};

export const destructureFitAndInseam = (lengthValue: string) => {
  // value comes back in format `Long 33"` or `Full-length` or `Cropped`
  const lastSpaceIndex = lengthValue.lastIndexOf(' ');
  const inseam = lengthValue.substring(lastSpaceIndex + 1);
  if (inseam.includes('"')) {
    const fit = lengthValue.substring(0, lastSpaceIndex);
    return { fit, inseam };
  }
  return { fit: lengthValue, inseam: undefined };
};

const SiblingsTagPrefix = 'sibling_product_identifier:';
